/* ============================================================
   App — MG Board v2 라우팅·상태 hub
   2026-05-13 views/components/types 분리 (단일 commit)
   분리 원칙: diff 최소 — 함수 본문·JSX·상태 한 글자도 수정 없음
   ============================================================ */
import { useEffect, useState, useMemo } from 'react'
import { CATEGORY_DEF, DEFAULT_OPEN_CATS } from './data'
import { useStats } from './useStats'
import type { ViewState } from './types/board'
import { Sidebar } from './components/layout/Sidebar'
import { SearchBar } from './components/layout/SearchBar'
import { StatsFooter } from './components/layout/StatsFooter'
import { HomeView } from './views/HomeView'
import { DomainView } from './views/DomainView'
import { CategoryView } from './views/CategoryView'
import { SchemaView } from './views/SchemaView'

export default function App() {
  const [view, setView] = useState<ViewState>({ mode: 'home' })
  const [filter, setFilter] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const init = new Set<string>()
    CATEGORY_DEF.forEach(c => {
      if (!DEFAULT_OPEN_CATS.has(c.name)) init.add(c.name)
    })
    return init
  })

  const stats = useStats()

  // 키보드 단축키
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        const inp = document.querySelector<HTMLInputElement>('.search-bar input')
        inp?.focus()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const openHome = () => setView({ mode: 'home' })
  const openDomain = (code: string) => setView({ mode: 'domain', domainCode: code })
  const openCategory = (name: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.delete(name)
      return next
    })
    setView({ mode: 'category', categoryName: name })
  }
  /* v2.5: openSchema 시그니처 확장 — sub_groups label/desc 선택 매개변수.
     CategoryView sub_groups 진입 시 동반 전달 → SchemaView 헤더 본질 회복.
     사이드바 schema_item 진입 시는 undefined (SCHEMA_LABELS fallback). */
  const openSchema = (
    catName: string,
    schemaName: string,
    subGroupLabel?: string,
    subGroupDesc?: string
  ) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.delete(catName)
      return next
    })
    setView({
      mode: 'schema',
      categoryName: catName,
      schemaName,
      subGroupLabel,
      subGroupDesc
    })
  }
  const toggleCollapse = (name: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const filteredCategories = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return CATEGORY_DEF
    return CATEGORY_DEF.map(c => ({
      ...c,
      schemas: c.schemas.filter(s => s.toLowerCase().includes(q)),
    })).filter(c => c.schemas.length > 0)
  }, [filter])

  return (
    <>
      <div className="draft-banner">
        ⚠ DRAFT — MG Board v2 · React + Vite · 의장님 v6.1 단일 소스 충실 이식 · 2026.05.12
      </div>

      <div className="app">
        {/* 사이드바 */}
        <Sidebar
          filteredCategories={filteredCategories}
          collapsed={collapsed}
          view={view}
          filter={filter}
          setFilter={setFilter}
          openHome={openHome}
          openCategory={openCategory}
          openSchema={openSchema}
          toggleCollapse={toggleCollapse}
        />

        {/* 메인 */}
        <main className="main">
          {/* 공통 헤더 — search-bar (모든 view 공통 노출, 2026-05-12 격상) */}
          <SearchBar />

          {/* view 본문 */}
          {view.mode === 'home' && <HomeView openDomain={openDomain} />}
          {view.mode === 'domain' && (
            <DomainView code={view.domainCode!} openHome={openHome} />
          )}
          {view.mode === 'category' && (
            <CategoryView
              catName={view.categoryName!}
              openHome={openHome}
              openSchema={openSchema}
            />
          )}
          {view.mode === 'schema' && (
            <SchemaView
              catName={view.categoryName!}
              schemaName={view.schemaName!}
              subGroupLabel={view.subGroupLabel}
              subGroupDesc={view.subGroupDesc}
              openHome={openHome}
              openCategory={openCategory}
            />
          )}

          {/* 공통 푸터 — stats-footer (모든 view 공통 노출, 2026-05-12 격상) */}
          <StatsFooter stats={stats} />
        </main>
      </div>
    </>
  )
}
